import { DisplayError } from "@/components/Error/Error";
import { CompleteSvg } from "@/components/Icon/Icon";
import { Loading } from "@/components/Loading/Loading";
import { Logo } from "@/components/Logo/Logo";
import { useUserEmailVerified } from "@/hooks/userEmailVerified";
import { OnboardingCard } from "@/pages/Onboarding/Components/OnboardingCard";
import { useVerifyEmailApiV1UsersUserIdVerifyEmailPostMutation } from "@/store/baseClientApi";
import { Box, Link, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

type emailResendStatus = "Sending" | "Success" | "Error" | undefined;

interface ResendEmailLinkProps {
  emailResendStatus: emailResendStatus;
  handleResendEmail: () => Promise<void>;
}
const ResendEmailLink: FC<ResendEmailLinkProps> = ({ emailResendStatus, handleResendEmail }) => {
  const { t } = useTranslation("onboarding");

  switch (emailResendStatus) {
    case "Success":
      return t("message.emailResendSuccess");
    case "Sending":
      return t("message.sendingEmail");
    case "Error":
      return t("message.resendEmailError");
    default:
      return (
        <Link sx={{ fontWeight: "bold" }} component="button" underline="hover" onClick={handleResendEmail}>
          {t("message.resendEmail")}
        </Link>
      );
  }
};

/**
 * Page-level component that blocks user progress until their Auth0 email address is verified.
 *
 * - If the email is not verified, it displays a message prompting the user to verify their email.
 * - If the email is verified, the page will show a success message and redirect to the dashboard.
 *
 * @component
 */
export const AwaitingEmailVerification: FC = () => {
  const [searchParams] = useSearchParams();
  const [emailResendStatus, setEmailResendStatus] = useState<emailResendStatus>();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("onboarding");
  const [resendEmail] = useVerifyEmailApiV1UsersUserIdVerifyEmailPostMutation();
  const { userId, emailVerified: isEmailVerified, isLoading, error } = useUserEmailVerified();

  const redirectRoute = searchParams.get("target");

  useEffect(() => {
    if (isEmailVerified) {
      // Redirect after timeout to allow success message
      setTimeout(() => {
        window.location.href = redirectRoute || "/cases"; // navigate using the browser so auth0 tokens get refeshed
      }, 2500);
    }
  }, [isEmailVerified]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResendEmail = async () => {
    if (!userId) {
      setEmailResendStatus("Error");
      return;
    }

    setEmailResendStatus("Sending");

    try {
      await resendEmail({ userId: userId }).unwrap();
      setEmailResendStatus("Success");
    } catch (_) {
      setEmailResendStatus("Error");
    }
  };

  if (error) {
    return <DisplayError error={error} />;
  }

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        height: "max-content",
        minHeight: "100vh",
        padding: sm ? "0" : "80px",
        overflowY: "auto",
        backgroundColor: (theme: Theme) => theme.surface.normal
      }}
    >
      <OnboardingCard>
        {isEmailVerified ? (
          <Box
            component="div"
            sx={{
              height: "52px",
              marginBottom: "24px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: (theme: Theme) => theme.palette.success.dark
            }}
          >
            <CompleteSvg height={"52px"} width={"52px"} />
          </Box>
        ) : (
          <Box
            component="div"
            sx={{
              height: "15px",
              marginBottom: "24px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <Logo width="171px" />
          </Box>
        )}
        {isLoading ? (
          <Loading />
        ) : (
          <Typography variant="subtitle1" textAlign={"center"} sx={{ whiteSpace: "pre-line" }}>
            {isEmailVerified ? (
              <Trans
                components={{
                  bold: <strong style={{ fontWeight: "bold" }} />
                }}
                i18nKey={"message.emailVerified"}
                t={t}
              />
            ) : (
              t("message.verifyEmail")
            )}
          </Typography>
        )}
        {!isEmailVerified && (
          <Typography
            textAlign={"center"}
            color="text.secondary"
            fontSize={"0.875em"}
            sx={{
              marginTop: "2em",
              marginBottom: "-1em"
            }}
          >
            <ResendEmailLink emailResendStatus={emailResendStatus} handleResendEmail={handleResendEmail} />
          </Typography>
        )}
      </OnboardingCard>
    </Box>
  );
};
